import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Text,
  VStack,
  useToast,
  Textarea
} from "@chakra-ui/react";
import { pxToRem } from "../../../utils/editor.ts";
import { useTranslation } from "react-i18next";
import { PaperService } from "../../../services/paperService.ts";
import { ClientError } from "../../../utils/clientError.ts";
import eventBus, { EventType } from "../../../hooks/useEventBus.ts";
import { SelectContent } from "../../../types/Writer/SelectContents.ts";
import { RephraseResult } from "../../../types/Writer/RephraseSentencesResult.ts";
import Reorganize from "./Reorganize.tsx";
import { Rephrase } from "./Rephrase.tsx";
import { Refine } from "./Refine.tsx";
import { ReorganizeSectionResultVersion } from "../../../types/Writer/ReorganizeSectionResult.ts";
import useOutlineStore from "../../../stores/modules/outline.ts";
import useAiTypeStore from "../../../stores/modules/aiType.ts";
import { convertTiptapToOutlineJson } from "../../../utils/tiptapConverter.ts";
import { findMatchingNodesFromSection } from "../../../utils/outlineNodeExtractor.ts";

interface RephraseProps {
  type: 'rephrase' | 'reorganize' | 'refine'
  isCalculated: boolean
  setIsCalculated: (isCalculated: boolean) => void
  updateOutline?: () => void
  editor?: any
}

export const AiPanelContainer: React.FC<RephraseProps> = (
  {
    editor,
    type,
    isCalculated,
    setIsCalculated,
    updateOutline
  }
) => {
  const { t } = useTranslation()
  const toast = useToast();
  const { outline, currentSectionIndex } = useOutlineStore()
  const lengthMap = [
    t("editor-editormodel-AI-RephraseLength-0"),
    t("editor-editormodel-AI-RephraseLength-1"),
    t("editor-editormodel-AI-RephraseLength-2"),
    t("editor-editormodel-AI-RephraseLength-3"),
    t("editor-editormodel-AI-RephraseLength-4"),
  ];

  const degreeMap = [
    t("editor-editormodel-AI-Rephrasedegree-0"),
    t("editor-editormodel-AI-Rephrasedegree-1"),
    t("editor-editormodel-AI-Rephrasedegree-2"),
    t("editor-editormodel-AI-Rephrasedegree-3"),
    t("editor-editormodel-AI-Rephrasedegree-4"),
  ];
  const lengthPositions = ['2rem', '5rem', '5.5rem', '9.8rem', '12rem']
  const degreePositions = ['1.3rem', '5.1rem', '5.7rem', '10rem', '13.1rem']
  const [currentLength, setCurrentLength] = useState<string>(lengthMap[2])
  const [currentDegreeValue, setCurrentDegreeValue] = React.useState<string>(degreeMap[2])
  const [isError, setIsError] = useState<boolean>(false)
  // rephrase or reorganize result
  const [resultList, setResultList] = useState<RephraseResult[] | ReorganizeSectionResultVersion[]>([])
  const { aiDataCache, setAiDataCache } = useAiTypeStore()
  const [defaultIndexs, setDefaultIndexs] = useState([])
  const [userPrompt, setUserPrompt] = useState<string>('')

  useEffect(() => {
    setUserPrompt('')
    if (outline) {
      // reset or reflex
      const id = outline.nodes[currentSectionIndex]?.id

      const cacheData = aiDataCache.get(id) || {}
      if (cacheData[type]?.length) {
        setResultList(cacheData[type])
        const indexs = Array.from(
          { length: cacheData[type].length },
          (_, i) => i
        )
        setDefaultIndexs(indexs)
      } else {
        setResultList([])
        setDefaultIndexs([])
      }
    }
  }, [type, currentSectionIndex])

  useEffect(() => {
    /**
     * receive editor rephrase event
     */
    eventBus.on(EventType.Rephrase, getPhrase)
    return () => {
      eventBus.off(EventType.Rephrase, getPhrase);
    }
  }, [])

  useEffect(() => {
    /**
     * receive editor refine event
     */
    eventBus.on(EventType.Refine, getRefine)
    return () => {
      eventBus.off(EventType.Refine, getRefine);
    }
  }, [])

  function setCache(rawData: RephraseResult[] | ReorganizeSectionResultVersion[]) {
    if (outline) {
      const id = outline.nodes[currentSectionIndex]?.id
      const data = { ...aiDataCache.get(id) }
      data[type] = rawData
      setAiDataCache(id, data)
    }
  }

  function getSelectedContentAsJSON(editor: any) {
    const { state } = editor;
    const { selection } = state;

    const slice = selection.content();
    return slice.content.toJSON?.() || [];
  }

  /**
   * rephrase api request
   */
  const getPhrase = async (selectContents?: SelectContent[]) => {
    // resetAIprogress(AIDataType.rephrase);
    localStorage.removeItem('select_content')
    const { outline, currentSectionIndex } = useOutlineStore.getState()
    if (outline) {
      setIsCalculated(true);
      const rephraseBody = {
        sectionNode: outline.nodes[currentSectionIndex],
        selectContents: selectContents || [],
        writingLevel: currentDegreeValue,
        writingLength: currentLength,
        userPrompt: userPrompt
      }
      try {
        const newPhrases = await PaperService.rephraseSentences(rephraseBody);
        const result = newPhrases.rephraseResult
        if (result?.length === 0) {
          toast({
              title: "no result",
              status: "error",
          });
        }
        setResultList(result)
        setCache(result)
        setDefaultIndexs(Array.from(
          { length: result.length },
          (_, i) => i
        ))
        setIsCalculated(false)
      } catch (error) {
        setIsCalculated(false)
        new ClientError(error).toast();
      }
    }
    // saveAIData(id, AIDataType.rephrase, aiData);
  };

  /**
   * reorganize
   */
  const requestReorganise = async () => {
    setIsCalculated(true)
    try {
      const result = await PaperService.reorganizeSection(outline.nodes[currentSectionIndex], currentDegreeValue, currentLength);
      const list = [result.reorganizeSectionOptionsData?.reorganized_structure_version_1]
      setResultList(list)
      setCache(list)
      setDefaultIndexs(Array.from(
        { length: list.length },
        (_, i) => i
      ))
      setIsCalculated(false)
    } catch (error) {
      setIsCalculated(false)
      new ClientError(error).toast();
    }
  }


  /**
   * refine api request
   * lick rephrase 
   */
  const getRefine = async (selectContents?: SelectContent[]) => {
    // resetAIprogress(AIDataType.rephrase);
    localStorage.removeItem('select_content')
    const { outline, currentSectionIndex } = useOutlineStore.getState()
    if (outline) {
      setIsCalculated(true);
      const rephraseBody = {
        sectionNode: outline.nodes[currentSectionIndex],
        selectContents: selectContents || [],
        writingLevel: currentDegreeValue,
        userPrompt: userPrompt
      }
      try {
        const newPhrases = await PaperService.refineSentences(rephraseBody);
        const result = newPhrases.touchupResult
        if (result?.length === 0) {
          toast({
              title: "no result",
              status: "error",
          });
        }
        setResultList(result)
        setCache(result)
        setDefaultIndexs(Array.from(
          { length: result.length },
          (_, i) => i
        ))
        setIsCalculated(false)
      } catch (error) {
        setIsCalculated(false)
        new ClientError(error).toast();
      }
    }
    // saveAIData(id, AIDataType.rephrase, aiData);
  };

  return (
    <VStack width={pxToRem(272)} data-ai-panel="true">
      {
        type !== "refine" ? (<> <Box
          mt={2}
          width={pxToRem(272)}
          paddingLeft={lengthPositions[lengthMap.indexOf(currentLength)]}
        >
          <Text
            aria-multiline={false}
            display="inline-block"
            pl={1}
            pr={1}
            border={"1px solid #ccc"}
            borderRadius={"3px"}
            bg={'#FFFFFF'}
          >
            {currentLength}
          </Text>
        </Box>
          <HStack
            border={"1px solid #ccc"}
            borderRadius={'0.5rem'}
            paddingRight={1}
            width={'100%'}
            bg={'#FFFFFF'}
          >
            <Box
              borderRight={"1px solid #ccc"}
              borderRadius={'0.5rem'}
              bg={'#FFFFFF'}
            >
              <Image
                m={2}
                w={pxToRem(24)}
                objectFit="cover"
                transform={'rotate(45deg)'}
                src="/static/ToolAI/length.png"
                alt="length"
              />
            </Box>
            {lengthMap.map((item, index) => (
              <IconButton
                key={index.toString() + "|" + item}
                bg={item == currentLength ? "#3898FF61" : "transparent"}
                size={"sm"}
                border={
                  item == currentLength ? "1px solid #3898FF" : undefined
                }
                borderRadius={"10px"}
                icon={
                  <Image
                    w={item == currentLength ? pxToRem(24) : "6px"}
                    objectFit="cover"
                    transform={'rotate(45deg)'}
                    src={
                      "/static/ToolAI/" +
                      (item == currentLength ? "length.png" : "dot.png")
                    }
                  />
                }
                aria-label={""}
                onClick={() => {
                  setCurrentLength(item);
                }}
              />
            ))}
          </HStack></>) : null
      }
      <Box
        mt={2}
        width={"100%"}
      >
        <Text
          aria-multiline={false}
          display="inline-block"
          pl={1}
          pr={1}
          border={"1px solid #ccc"}
          bg={'#FFFFFF'}
          borderRadius={"3px"}
          whiteSpace={'nowrap'}
          marginLeft={
            degreePositions[degreeMap.indexOf(currentDegreeValue)]
          }
        >
          {currentDegreeValue}
        </Text>
      </Box>
      <HStack
        border={"1px solid #ccc"}
        borderRadius={"0.5rem"}
        paddingRight={1}
        bg={'#FFFFFF'}
        width={'100%'}
      >
        <Box
          borderRight={"1px solid #ccc"}
          borderRadius={"0.5rem"}
          bg={'#FFFFFF'}
        >
          <Image
            m={2}
            w={pxToRem(24)}
            objectFit="cover"
            src="/static/ToolAI/degree.png"
          />
        </Box>
        {degreeMap.map((item) => (
          <IconButton
            key={item}
            bg={item == currentDegreeValue ? "#3898FF61" : "transparent"}
            size={"sm"}
            border={
              item == currentDegreeValue
                ? "1px solid #3898FF"
                : undefined
            }
            borderRadius={"10px"}
            icon={
              <Image
                w={item == currentDegreeValue ? pxToRem(24) : "6px"}
                objectFit="cover"
                src={
                  "/static/ToolAI/" +
                  (item == currentDegreeValue ? "degree.png" : "dot.png")
                }
              />
            }
            aria-label={""}
            onClick={() => {
              setCurrentDegreeValue(item);
            }}
          />
        ))}
      </HStack>
      <Button
        bg={"#FFF"}
        border={"1px solid #3898FF"}
        width={pxToRem(118)}
        height={pxToRem(34)}
        textColor={"#3898FF"}
        borderRadius={pxToRem(6)}
        _hover={{ bg: '#1D8AFF', color: '#FFF' }}
        onClick={(e) => {
          e.stopPropagation()
          if (type === 'rephrase' || type === 'refine') {
            const list = getSelectedContentAsJSON(editor) || []

            // Step 1: Convert Tiptap selection to Outline format (with potentially wrong IDs)
            const tiptapResult = convertTiptapToOutlineJson({
              content: [
                {
                  type: "heading",
                  attrs: {
                    textAlign: null,
                    level: 1,
                    color: "#1D8AFF",
                    id: 1,
                  },
                  content: [
                    {
                      type: "text",
                      text: "New Section",
                    },
                  ],
                },
                ...list,
              ],
            }).children;

            // Add select_content to each node
            tiptapResult?.forEach((item) => {
              if (['ul', 'ol'].includes(item.type)) {
                const processChildren = (node: any) => {
                  if (node?.children?.length > 0) {
                    if (!['ul', 'ol'].includes(node.type)) {
                      node.select_content = node.content.replace(
                        /<\/(em|strong|u)>$/,
                        ""
                      );
                    }
                    node.children.forEach((child: any) => processChildren(child));
                  } else {
                    node.select_content = node.content;
                  }
                }
                processChildren(item);
              } else {
                item.select_content = item.content?.replace(
                  /<\/(em|strong|u)>$/,
                  ""
                ) || '';
              }
            });

            if (tiptapResult?.length === 0) {
              toast({
                  title: "Please select content",
                  status: "error",
              });
              return
            }

            // Step 2: Get the current sectionNode from outline store
            const sectionNode = outline?.nodes?.[currentSectionIndex];

            // Step 3: Find matching nodes with correct IDs from sectionNode
            let result = tiptapResult;
            if (sectionNode && tiptapResult?.length) {
              const matchedNodes = findMatchingNodesFromSection(sectionNode, tiptapResult);
              if (matchedNodes.length > 0) {
                result = matchedNodes;
              }
            }

            type === 'rephrase' ? getPhrase(result) : getRefine(result);
          }
          if (type === 'reorganize') {
            requestReorganise()
          }
        }}>
        {type === 'rephrase' ? t("editor-editormodel-AI-Rephrase") :
          type === 'reorganize' ? t("editor-editormodel-AI-Reorganize") : t('editor-editormodel-AI-Refine')}
      </Button>
      {type !== 'reorganize' && <Textarea
        placeholder={`If the text generated below does not meet your expectations, kindly provide specific requirements and click "${type === 'rephrase' ? t("editor-editormodel-AI-Rephrase") : t('editor-editormodel-AI-Refine')}" to generate a new version.`}
        value={userPrompt}
        onChange={(e) => setUserPrompt(e.target.value)}
        mt={2}
        width={pxToRem(272)}
        minHeight={pxToRem(100)}
        border={"1px solid #D3D7E2"}
        fontSize={'sm'}
        bg={'#FFFFFF'}
        p="8px"
        borderRadius={'8px'}
      />}

      {isCalculated ? (
        <VStack
          mt={2}
          width={pxToRem(272)}
          height={pxToRem(240)}
          border={"1px solid #D3D7E2"}
          borderRadius={pxToRem(12)}
          bgColor={"#FFFFFF"}
        >
          <Image
            mt={4}
            w={pxToRem(58)}
            h={"1rem"}
            objectFit="contain"
            src={"/static/doenba-logo-blue.png"}
            mr={pxToRem(170)}
          />
          <Image
            mt={4}
            w={pxToRem(32)}
            objectFit="cover"
            src="/static/loading-animate.svg"
            transform={"scale(2)"}
          />
          <Text textAlign="center" mt={4} p={2}>{type === 'rephrase' ? t("editor-editormodel-Rephrasing") : type === 'reorganize' ? t('editor-editormodel-reorganize-loading') : t('editor-editormodel-Refineing')}</Text>
          <IconButton
            bg="transparent"
            size={"xs"}
            onClick={(e) => {
              e.stopPropagation()
              setIsCalculated(false);
              setIsError(false);
            }}
            icon={
              <Image
                w={"14px"}
                objectFit="contain"
                src={
                  "/static/ToolAI/" +
                  (isError ? "recalculate.svg" : "cancelCal.svg")
                }
                alt="cancel"
              />
            }
            aria-label="cancel"
          />
        </VStack>
      ) : (
        <Accordion
          allowMultiple
          defaultIndex={defaultIndexs}
        >
          {resultList.map((result, index) => {
            if (type === 'refine' && !result.select_content_touch_up_result) {
              return null;
            }

            if (type === 'rephrase' && !result.select_content_rephrased_result) {
              return null;
            }

            return (
              <AccordionItem
                mt={2}
                width={pxToRem(272)}
                key={index.toString() + "|" + result.id}
                border={"1px solid #D3D7E2"}
                borderRadius={pxToRem(12)}
                p={2}
                bgColor={"#FFFFFF"}
              >
                <VStack>
                  <HStack>
                    <Image
                      w={pxToRem(58)}
                      h={"1rem"}
                      objectFit="cover"
                      src={"/static/doenba-logo-blue.png"}
                      mr={pxToRem(130)}
                    />
                    <AccordionButton _hover={{ bg: 'transparent' }}>
                      <Image width={pxToRem(24)} src={"/static/ToolAI/expand.png"} />
                    </AccordionButton>
                  </HStack>
                  <AccordionPanel>
                    {/** rephrase result or reorganize result */}
                    {type === 'rephrase' ? (<Rephrase result={result} />) :
                      type === 'reorganize' ? (
                        <Reorganize
                          result={result}
                          index={index}
                          currentLength={currentLength}
                          currentDegree={currentDegreeValue}
                          updateOutline={updateOutline}
                        />) : (<Refine result={result} />)}
                  </AccordionPanel>
                </VStack>
              </AccordionItem>
            )
          })}
        </Accordion>
      )}
    </VStack>
  )
}